import { NextPage } from 'next';
import { useEffect } from 'react';
import { useRedirect } from '@utils/redirect';
import { useAuthentication } from '@context/businessContainer';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { useTranslations } from 'next-intl';
import { LFLayoutPage } from '@utils/layout';

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

interface INextSeoProps {
  title?: Extract<
    keyof Omit<
      IntlMessages['Account'],
      | 'subscriptionStatus'
      | 'manageMySubscriptionSection'
      | 'orderTrackingModal'
      | 'birthDateModal'
      | 'changeMySubscriptionModal'
      | 'changeMySubscriptionSuccessModal'
      | 'latcBanner'
    >,
    string
  >; // Use Generic type would be bettet @intl-ts
  noindex?: boolean;
}

const withUnauthentication = <P extends object, IP = P>(
  WrappedComponent: LFLayoutPage<P, IP> | NextPage<P, IP>,
  nextSeoProps?: INextSeoProps,
  withLoader = false,
  redirectFunction?: (isMember: boolean) => void,
) => {
  const WithUnauthentication: LFLayoutPage<P> = (props) => {
    const { authUser, isLoading, isLogoutPending, isMember } =
      useAuthentication();
    const router = useRouter();
    const tAccount = useTranslations('Account');

    const { redirectToHomePageOrReferrer } = useRedirect();

    useEffect(() => {
      if (!isLoading && authUser && !isLogoutPending) {
        redirectFunction
          ? redirectFunction(isMember)
          : redirectToHomePageOrReferrer(authUser);
      }
    }, [isLoading]);

    if (withLoader && (isLoading || authUser)) {
      return null;
    }

    return (
      <>
        <NextSeo
          noindex={nextSeoProps?.noindex}
          title={nextSeoProps?.title && tAccount(nextSeoProps.title)}
          canonical={`${appBaseUrl}${router.pathname}`}
          openGraph={{
            url: `${appBaseUrl}${router.pathname}`,
            title: nextSeoProps?.title && tAccount(nextSeoProps.title),
          }}
        />
        <WrappedComponent {...props} />
      </>
    );
  };

  for (const property in WrappedComponent) {
    WithUnauthentication[property] = WrappedComponent[property];
  }

  return WithUnauthentication;
};

export default withUnauthentication;
